import React, { useEffect, useRef } from 'react';
import { useContent } from '../contexts/ContentContext';
import { useTooltip } from '../contexts/TooltipContext';
import clsx from 'clsx'; // Import clsx for merging class names
import { MonthLink } from '../services/api';

interface PaginationProps {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    newerMonth?: MonthLink | null;
    olderMonth?: MonthLink | null;
    gridWidth?: number | null;
    className?: string; // Add className prop
}

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalItems,
    itemsPerPage,
    onPageChange,
    gridWidth,
    className
}) => {
    const { content, language, t } = useContent();
    const { showTooltip, hideTooltip } = useTooltip();
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const normalizedCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
    const hasPageControls = totalItems > itemsPerPage;
    const pendingScrollRef = useRef(false);

    const handlePrev = () => {
        if (normalizedCurrentPage > 1) {
            pendingScrollRef.current = true;
            onPageChange(normalizedCurrentPage - 1);
        }
    };

    const handleNext = () => {
        if (normalizedCurrentPage < totalPages) {
            pendingScrollRef.current = true;
            onPageChange(normalizedCurrentPage + 1);
        }
    };

    useEffect(() => {
        if (!pendingScrollRef.current) {
            return;
        }
        pendingScrollRef.current = false;

        if (window.location.hash) {
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        }

        requestAnimationFrame(() => {
            const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
            window.scrollTo({ top: 0, behavior });
        });
    }, [currentPage]);

    if (!hasPageControls) {
        return null;
    }

    const pageLabel = t('common.pageCount', { current: normalizedCurrentPage, total: totalPages });

    const pageButtonClassName = "inline-flex h-10 w-10 items-center justify-center rounded-md bg-gray-300 text-lg font-bold text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-600 dark:text-gray-100 dark:disabled:opacity-50 ivory:bg-coffee-300 ivory:text-coffee-900 transform transition-transform duration-200 ease-in-out hover:-translate-y-0.5";
    const widthStyle = gridWidth ? { width: `${gridWidth}px`, maxWidth: '100%' } : undefined;

    return (
        <div
            className={clsx("mt-8 flex w-full items-center justify-center gap-3", className)}
            style={widthStyle}
        >
            <div className="flex items-center justify-center gap-3">
                <button
                    type="button"
                    onClick={handlePrev}
                    disabled={normalizedCurrentPage === 1}
                    aria-label={content.ui[language].pagination.previous}
                    title={content.ui[language].pagination.previous}
                    className={pageButtonClassName}
                    onMouseEnter={(e) => showTooltip(content.tooltips[language].pagination.prev, e)}
                    onMouseLeave={hideTooltip}
                >
                    <span aria-hidden="true">&#9664;</span>
                </button>
                <span className="min-w-[7rem] text-center text-sm font-semibold text-gray-700 dark:text-gray-300 ivory:text-coffee-700 sm:text-base">
                    {pageLabel}
                </span>
                <button
                    type="button"
                    onClick={handleNext}
                    disabled={normalizedCurrentPage === totalPages}
                    aria-label={content.ui[language].pagination.next}
                    title={content.ui[language].pagination.next}
                    className={pageButtonClassName}
                    onMouseEnter={(e) => showTooltip(content.tooltips[language].pagination.next, e)}
                    onMouseLeave={hideTooltip}
                >
                    <span aria-hidden="true">&#9654;</span>
                </button>
            </div>
        </div>
    );
};

export default Pagination;
